import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChange,
  inject
} from '@angular/core';
import { NgbDropdownItem, NgbPagination, NgbPaginationNext, NgbPaginationPrevious, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent, DropdownButtonComponent } from '../../button';
import { PAGE_SCROLL_CONTAINER_SELECTOR } from '../../injection-tokens';
import { SHARED_PERSISTENT_STATE, SharedPersistentStateEnum, SharedPersistentStateService, ViewMode } from '../../services';
import { TableControlDirective } from '../directives/table-control.directive';
import { TableFilterDirective } from '../directives/table-filter.directive';
import { FiltersGroupDirective } from '../directives/table-filters-group.directive';
import { TableNavContentDirective } from '../directives/table-nav-content.directive';
import { PaginationOptions } from '../models/pagination-options';

@Component({
  selector: 'mbs-table-nav',
  templateUrl: './table-nav.component.html',
  imports: [
    ButtonComponent,
    DropdownButtonComponent,
    NgbDropdownItem,
    NgbPagination,
    NgbPaginationNext,
    NgbPaginationPrevious,
    NgbTooltip,
    NgTemplateOutlet
  ],
  host: {
    '(window:resize)': 'onResize($event)'
  }
})
export class TableNavComponent implements OnChanges, OnInit {
  private sharedPersistent = inject<SharedPersistentStateService>(SHARED_PERSISTENT_STATE);
  private pageScrollContainerSelector = inject(PAGE_SCROLL_CONTAINER_SELECTOR);

  public readonly sharedPersistentStateEnum = SharedPersistentStateEnum;
  public readonly elementSelectors = {
    id: {
      refreshButton: 'table-nav-refresh-button'
    }
  };
  /**
   * Pagination options. For disable pagination set `pageSize=0` or `dataSize=0`
   * * `maxSize` - Visible pages number. Default: `3`
   * * `rotate` - Whether to rotate pages when `maxSize` > number of pages.
   * The current page always stays in the middle if true. Default: `false`
   * * `pageSize` - The number of items per page. Default: `20`. -1 means 'Show all'.
   * * `dataSize` - Total number of items.
   * * `pageSizeList` - Array of numbers to choose `pageSize` from. Default: `null`.
   * *  It is possible to use '-1' in 'pageSizeList'.  -1 means 'Show all'.
   * * `page` - current page. Default: `1`;
   */
  @Input() paginationOptions: PaginationOptions;
  public enableSwitcher = true;
  public paginationOptionsDefault: PaginationOptions = {
    maxSize: 3,
    rotate: false,
    pageSize: 20,
    dataSize: 0,
    pageSizeList: null,
    page: 1
  };
  public paginationOptionsInternal: PaginationOptions = this.paginationOptionsDefault;
  /**
   * Disable switching from selected view to different 'table' or 'grid'
   */
  @Input() disableViewSwitch = false;
  public myViewMode: ViewMode = this.sharedPersistent.data.viewTableGridMode || SharedPersistentStateEnum.table;
  public lastViewMode: ViewMode = this.myViewMode;

  /**
   * Can manually switch view mode;
   */
  @Input() switcherView = false;
  @Input() showExportToCSVButton = false;
  @Input() showRefreshButton = false;
  @Input() refreshing = false;
  @Input() exportButtonName = 'Export to CSV';

  @Input() public showNav =
    (this.paginationOptionsInternal.pageSize !== 0 && this.paginationOptionsInternal.dataSize > 0) ||
    (this.enableSwitcher && this.switcherView);

  @Input() wideContentMode = false; // col-8 and col-4
  @Input() fullContentWidth = false; // col-12

  @ContentChildren(TableFilterDirective, {
    read: TableFilterDirective,
    descendants: false
  })
  filterTemplates: QueryList<TableFilterDirective>;
  @Input() filters: QueryList<TableFilterDirective>;

  get myFilters(): QueryList<TableFilterDirective> {
    return this.filterTemplates.length > 0 ? this.filterTemplates : this.filters;
  }

  get hasFilters(): boolean {
    return !!(this.myFilters?.length > 0 || this.myContent || this.filtersGroup);
  }

  @ContentChild(TableNavContentDirective, { read: TableNavContentDirective, static: false })
  customContentTemplate: TableNavContentDirective;

  @ContentChild(FiltersGroupDirective, { read: FiltersGroupDirective, static: true })
  filtersGroup: FiltersGroupDirective;

  @Input() customContent: TableNavContentDirective;

  public get myContent(): TableNavContentDirective {
    return this.customContentTemplate ? this.customContentTemplate : this.customContent;
  }

  /**
   * Disable moving to top of the page on page change
   */
  @Input() disableScrollTopOnPageChange = false;

  @ContentChildren(TableControlDirective, {
    read: TableControlDirective,
    descendants: false
  })
  controlTemplates: QueryList<TableControlDirective>;
  @Input() customControls: QueryList<TableControlDirective>;

  @Output() viewChange: EventEmitter<SharedPersistentStateEnum> = new EventEmitter<SharedPersistentStateEnum>();
  @Output() pageSizeChange: EventEmitter<PaginationOptions> = new EventEmitter<PaginationOptions>();
  @Output() pageSwitch: EventEmitter<PaginationOptions> = new EventEmitter<PaginationOptions>();

  /**
   * Emits on refresh button click with no data;
   */
  @Output() refresh: EventEmitter<any> = new EventEmitter<any>();
  /**
   * Emits on refresh button click with no data;
   */
  @Output() export: EventEmitter<any> = new EventEmitter<any>();

  onResize(event: Event): void {
    if (event.target instanceof Window && !this.disableViewSwitch) {
      queueMicrotask(() => {
        if ((event.target as Window).innerWidth < 576) {
          this.enableSwitcher = false;
          this.myViewMode = SharedPersistentStateEnum.grid;
          this.viewChange.emit(this.lastViewMode);
        } else {
          this.enableSwitcher = true;
          this.myViewMode = this.lastViewMode;
          this.viewChange.emit(this.myViewMode);
        }
      });
    }
  }

  /**
   * @default ['table']
   * @param {ViewMode} mode
   */
  @Input() set viewMode(mode: ViewMode) {
    this.myViewMode = mode;
  }

  public get needToShowPaginationColumn(): boolean {
    return (
      this.showRefreshButton ||
      this.showExportToCSVButton ||
      this.customAppendControls.length > 0 ||
      this.customPrependControls.length > 0 ||
      (this.paginationOptionsInternal.pageSize < this.paginationOptionsInternal.dataSize && this.paginationOptionsInternal.dataSize > 0) ||
      (this.enableSwitcher && this.switcherView)
    );
  }

  private myControls(): QueryList<TableControlDirective> {
    return this.controlTemplates.length > 0 ? this.controlTemplates : this.customControls;
  }

  public get customPrependControls(): TableControlDirective[] {
    return this.myControls() ? this.myControls().filter((item) => item.mode === 'prepend') : [];
  }

  public get customAppendControls(): TableControlDirective[] {
    return this.myControls() ? this.myControls().filter((item) => item.mode === 'append') : [];
  }

  public get showPagination(): boolean {
    return (
      this.paginationOptionsInternal.pageSize > 0 &&
      this.paginationOptionsInternal.dataSize > 0 &&
      (this.paginationOptionsInternal.pageSize < this.paginationOptionsInternal.dataSize || this.paginationOptionsInternal.page > 1)
    );
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }): void {
    if (changes.paginationOptions) {
      this.paginationOptionsInternal = Object.assign({}, this.paginationOptionsDefault, this.paginationOptions);
    }
  }

  ngOnInit(): void {
    if (
      this.paginationOptions &&
      this.paginationOptions.pageSize &&
      this.paginationOptions.pageSizeList != null &&
      !this.paginationOptions.pageSizeList.includes(this.paginationOptions.pageSize)
    ) {
      this.paginationOptions.pageSize = this.paginationOptions.pageSizeList[0];
    }
    if (window.innerWidth < 576) {
      this.enableSwitcher = false;
      this.myViewMode = SharedPersistentStateEnum.grid;
    }
  }

  /**
   * @ignore
   * @param {number} page
   * @return {void}
   */
  onPageChange(page: number): void {
    const moveToTop = this.paginationOptionsInternal.page !== page;

    this.paginationOptionsInternal.page = page;
    this.pageSwitch.emit(this.paginationOptionsInternal);

    if (!this.disableScrollTopOnPageChange && moveToTop) {
      try {
        this.pageScrollContainerSelector &&
          document.querySelector(this.pageScrollContainerSelector)?.scrollTo({ top: 0, behavior: 'smooth' });

        return window?.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        return;
      }
    }
  }

  /**
   * @ignore
   * @param {SharedPersistentStateEnum} mode
   */
  onViewChange(mode: SharedPersistentStateEnum): void {
    this.sharedPersistent.data.viewTableGridMode = mode;
    this.myViewMode = mode;
    this.lastViewMode = mode;
    this.viewChange.emit(this.myViewMode);
  }

  handleChangePageSize(pageSize: number): void {
    this.paginationOptionsInternal = Object.assign({}, this.paginationOptionsDefault, this.paginationOptions, { pageSize });
    this.pageSizeChange.emit(this.paginationOptionsInternal);
  }

  getPageSizeText(pageSize: number): string {
    return this.getDefaultPageSizeText(pageSize);
  }

  getPageSizeMenuText(pageSize: number): string {
    return this.paginationOptionsInternal.pageSizeMenuFormatter
      ? this.paginationOptionsInternal.pageSizeMenuFormatter(pageSize)
      : this.getDefaultPageSizeText(pageSize);
  }

  private getDefaultPageSizeText(pageSize: number): string {
    return pageSize === -1 ? 'Show all' : `${pageSize} per page`;
  }
}
